// =============================================================================
// TerminalChargePanel — "Afrekenen op de pinterminal" (WisePOS E)
// =============================================================================
// Shared by the walk-in modal (right after a "Pinterminal (POS)" booking is
// created), BookingDetailScreen (unpaid bookings) and CheckinFlowModal
// (pay-and-check-in on scan). Calls booking-terminal `start` once, then polls
// `status` every second. Advances on pi_status 'succeeded' (Stripe) or the
// booking reading 'paid' (webhook) — same rule as the kiosk. Decline /
// reader-timeout → retry.
//
// The payment itself goes through a ChargeDriver (api/terminal.ts): without
// a `driver` prop the panel charges the booking via booking-terminal; the gift
// card load (GiftCardLoadAction) passes its own driver for booking-giftcard.
//
// Three different exits, deliberately not the same thing:
//   "Boeking annuleren"  → frees the reader, cancels the booking outright.
//   "Anders afrekenen"   → frees the reader AND pins the booking (keep_unpaid),
//                          so it survives to be settled in cash / by betaallink.
//   ✕ / Escape           → frees the reader, decides nothing. A provisional
//                          booking then expires and the POS sweeper cancels it.
// =============================================================================
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  bookingChargeDriver,
  terminalErrorMessage,
  type ChargeDriver,
  type ChargeStatus,
  type TerminalError,
} from '../api/terminal';
import { cancelBooking } from '../api/cancel-refund';

const POLL_MS = 1000;
// A real tap lands within ~20 s; Stripe can still report a powered-off reader
// as online for minutes, so a short soft timeout is what tells staff the
// reader is not actually taking the payment.
const WAITING_TIMEOUT_MS = 45_000;
const IDLE_DECLINE_POLLS = 3;
// Backstop after the soft timeout: a walk-away leaves Stripe's reader action
// `in_progress` forever (it is not a `failed` action, so booking-terminal's
// eager clear never sees it), and a reader still holding someone else's action
// answers `terminal_reader_busy` to the NEXT counter sale — a stuck prompt
// blocks the till, it is not cosmetic. Give a slow customer room to find their
// card, then release the reader on our own. Total wait = 45 s + this.
const READER_RELEASE_MS = 120_000;

// Imperative handle so a modal shell can free the reader BEFORE it unmounts
// this panel (✕ / Escape). Clearing in an unmount effect instead would fire
// spuriously under React StrictMode, which double-invokes effects in dev and
// would cancel a payment that was just pushed.
export type TerminalChargeHandle = {
  clearReader: () => Promise<void>;
};

export type TerminalChargeOutcome = {
  paid: boolean;
  checkedIn: boolean | null;
};

interface Props {
  // Required unless `driver` is given.
  bookingId?: string;
  // Charge something other than a booking (gift card load). Must be stable
  // for the panel's lifetime (create it once with useMemo/useState).
  driver?: ChargeDriver;
  bookingNumber?: string | null;
  // Departure to check in once paid (the server performs it; we surface it).
  checkinDepartureId?: string | null;
  // Fired once when the payment is confirmed (and, if requested, check-in state known).
  onSettled: (outcome: TerminalChargeOutcome) => void;
  // Operator gave up ("Anders afrekenen"). Booking stays unpaid (cash /
  // betaallink follow from the booking page).
  onAbandon: () => void;
  // When set, "Boeking annuleren" is offered: clears the reader AND cancels
  // the booking (scope: whole booking, no refund - nothing was paid). Passed
  // by the walk-in success view + booking page; NOT by the scan flow.
  cancelDepartureId?: string | null;
  onCancelledBooking?: () => void;
  // Quick walk-in flow: no "Anders afrekenen" escape — a failed payment is
  // retried or the booking is cancelled, never left behind as unpaid.
  hideAbandon?: boolean;
  // Optional: skip the `start` call because the caller already started.
  autoStart?: boolean;
  // The caller created this booking purely to carry this payment (walk-in
  // modal). Abandoning the payment may then cancel the booking: booking-terminal
  // stamps a provisional window and the POS sweeper cleans it up if nobody
  // finishes. Off for bookings that already existed.
  provisional?: boolean;
}

type Phase = 'starting' | 'busy' | 'waiting' | 'declined' | 'timeout' | 'start_failed' | 'settling' | 'done';

// Reader busy with another payment (kiosk visitor / colleague): re-try the
// push every BUSY_RETRY_MS, up to BUSY_MAX_ATTEMPTS, before asking the
// operator what to do.
const BUSY_RETRY_MS = 5000;
const BUSY_MAX_ATTEMPTS = 12;

export const TerminalChargePanel = forwardRef<TerminalChargeHandle, Props>(function TerminalChargePanel({
  bookingId,
  driver,
  bookingNumber,
  checkinDepartureId,
  onSettled,
  onAbandon,
  cancelDepartureId = null,
  onCancelledBooking,
  hideAbandon = false,
  autoStart = true,
  provisional = false,
}: Props, ref) {
  const [phase, setPhase] = useState<Phase>(autoStart ? 'starting' : 'waiting');
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [readerLabel, setReaderLabel] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [declineText, setDeclineText] = useState<string | null>(null);
  const [checkedIn, setCheckedIn] = useState<boolean | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  // Reader freed by the backstop below — the prompt is gone from the device,
  // so there is nothing left to poll for until the operator retries.
  const [readerReleased, setReaderReleased] = useState(false);
  const idlePolls = useRef(0);
  const inFlight = useRef(false);
  const settledRef = useRef(false);
  const busyAttempts = useRef(0);
  const busyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmounted = useRef(false);
  const drv = useMemo<ChargeDriver>(
    () => driver ?? bookingChargeDriver(bookingId ?? '', checkinDepartureId ?? null, provisional),
    [driver, bookingId, checkinDepartureId, provisional],
  );
  const abandonHidden = hideAbandon || !drv.keepUnpaid;

  const start = useCallback(async () => {
    setPhase((p) => (p === 'busy' ? 'busy' : 'starting'));
    setErrorText(null);
    setDeclineText(null);
    const res = await drv.start();
    if (unmounted.current) return;
    if (!res.ok || !res.data) {
      if (res.error?.error === 'reader_busy' && busyAttempts.current < BUSY_MAX_ATTEMPTS) {
        // Someone else is paying on this reader — wait and re-try quietly.
        busyAttempts.current += 1;
        setPhase('busy');
        busyTimer.current = setTimeout(() => void start(), BUSY_RETRY_MS);
        return;
      }
      setErrorText(
        res.error?.error === 'reader_busy'
          ? 'De pinterminal blijft bezet met een andere betaling.'
          : terminalErrorMessage(res.error),
      );
      setPhase('start_failed');
      return;
    }
    busyAttempts.current = 0;
    setAmountCents(res.data.amount_due_cents);
    setReaderLabel(res.data.reader_label ?? res.data.reader_id);
    idlePolls.current = 0;
    setReaderReleased(false);
    setPhase('waiting');
    setAttempt((a) => a + 1);
  }, [drv]);

  useEffect(() => {
    unmounted.current = false;
    if (autoStart) void start();
    return () => {
      unmounted.current = true;
      if (busyTimer.current) clearTimeout(busyTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while waiting / settling — and THROUGH the soft timeout, until the
  // reader is actually freed. The 45 s timeout only means "we stopped
  // expecting it"; the payment is still live on the device, so a late tap must
  // still settle the booking instead of leaving the operator staring at
  // "niets ontvangen" while the money lands.
  useEffect(() => {
    const pollThisPhase =
      phase === 'waiting' || phase === 'settling' || (phase === 'timeout' && !readerReleased);
    if (!pollThisPhase) return;
    let stopped = false;

    const finish = (status: ChargeStatus) => {
      if (settledRef.current) return;
      const needsCheckin = !!checkinDepartureId;
      const ci = status.checked_in;
      setCheckedIn(ci);
      // With a check-in requested, keep polling briefly until the server
      // reports it (the status call itself performs it once paid).
      if (needsCheckin && ci !== true) {
        setPhase('settling');
        return;
      }
      settledRef.current = true;
      setPhase('done');
      onSettled({ paid: true, checkedIn: needsCheckin ? ci : null });
    };

    const tick = async () => {
      if (stopped || inFlight.current) return;
      inFlight.current = true;
      try {
        const res = await drv.status();
        if (stopped) return;
        if (!res.ok || !res.data) {
          // transient — keep polling
          return;
        }
        const s = res.data;
        if (s.amount_due_cents != null && amountCents == null) setAmountCents(s.amount_due_cents);
        if (s.settled) {
          finish(s);
          return;
        }
        if (s.reader_action_status === 'failed') {
          idlePolls.current = 0;
          setDeclineText(s.reader_action_failure_message ?? s.reader_action_failure_code ?? null);
          setPhase('declined');
          return;
        }
        if (s.pi_status === 'requires_payment_method' && s.reader_action_status === null) {
          idlePolls.current += 1;
          if (idlePolls.current >= IDLE_DECLINE_POLLS) {
            setDeclineText(null);
            setPhase('declined');
          }
          return;
        }
        idlePolls.current = 0;
      } finally {
        inFlight.current = false;
      }
    };

    void tick();
    const interval = setInterval(() => void tick(), POLL_MS);
    return () => {
      stopped = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, attempt, drv, checkinDepartureId, readerReleased]);

  // Soft timeout while waiting for a tap, with a visible countdown.
  useEffect(() => {
    if (phase !== 'waiting') {
      setSecondsLeft(null);
      return;
    }
    const startedAt = Date.now();
    setSecondsLeft(Math.round(WAITING_TIMEOUT_MS / 1000));
    const ticker = setInterval(() => {
      const left = Math.max(0, Math.round((WAITING_TIMEOUT_MS - (Date.now() - startedAt)) / 1000));
      setSecondsLeft(left);
      if (left <= 0) setPhase((p) => (p === 'waiting' ? 'timeout' : p));
    }, 1000);
    return () => clearInterval(ticker);
  }, [phase, attempt]);

  // Backstop: the soft timeout passed and neither the customer nor the
  // operator did anything. Free the reader ourselves so the next sale is not
  // refused with "bezet". Best-effort and client-side: if the tab is closed
  // first nothing fires here and the reader stays held until the next
  // start/retry/cancel trips clearStaleReaderAction server-side.
  useEffect(() => {
    if (phase !== 'timeout' || readerReleased) return;
    const timer = setTimeout(() => {
      void (async () => {
        const res = await drv.cancel();
        if (unmounted.current) return;
        if (res.ok) setReaderReleased(true);
      })();
    }, READER_RELEASE_MS);
    return () => clearTimeout(timer);
  }, [phase, readerReleased, drv]);

  const handleRetry = async () => {
    setBusy(true);
    setErrorText(null);
    try {
      if (phase === 'start_failed') {
        await start();
        return;
      }
      const res = await drv.retry();
      if (!res.ok) {
        const err: TerminalError | null = res.error;
        if (err?.error === 'pi_canceled' || err?.error === 'no_payment_intent') {
          await start();
          return;
        }
        setErrorText(terminalErrorMessage(err));
        return;
      }
      idlePolls.current = 0;
      setDeclineText(null);
      setReaderReleased(false);
      setPhase('waiting');
      setAttempt((a) => a + 1);
    } finally {
      setBusy(false);
    }
  };

  // Free the reader. Called by the panel's own exits AND by the modal shells
  // through the imperative handle when the operator closes with ✕ / Escape —
  // closing the window means "I am done with this payment" just as much as
  // pressing Annuleren does, and used to leave the prompt up indefinitely.
  // Returns an operator-facing problem, or null when the reader is free.
  const clearReader = async (): Promise<string | null> => {
    if (busyTimer.current) clearTimeout(busyTimer.current);
    // Settled payments left nothing on the device. Everything else may still
    // hold a prompt — including 'starting', where the push can land after us.
    if (phase === 'done' || phase === 'settling' || readerReleased) return null;
    const res = await drv.cancel();
    // canceled:false means the reader already moved on to another booking's
    // payment, so our prompt is gone either way. Only a hard failure — an
    // offline or unreachable reader — is worth telling the operator about.
    return res.ok ? null : terminalErrorMessage(res.error);
  };

  useImperativeHandle(ref, () => ({
    clearReader: async () => {
      await clearReader();
    },
  }));

  // "Anders afrekenen" is the deliberate counterpart to closing the window:
  // it frees the reader AND pins the booking, so the POS sweeper leaves it
  // alone and it can be settled later in cash or by betaallink. Closing with
  // ✕ deliberately does NOT pin — an abandoned payment takes its booking.
  const handleAbandon = async () => {
    setBusy(true);
    setErrorText(null);
    try {
      if (busyTimer.current) clearTimeout(busyTimer.current);
      if (!drv.keepUnpaid) return;
      const res = await drv.keepUnpaid();
      if (!res.ok) {
        setErrorText(
          `${terminalErrorMessage(res.error)} De boeking is niet vastgezet — probeer opnieuw.`,
        );
        return;
      }
      onAbandon();
    } finally {
      setBusy(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!cancelDepartureId) return;
    if (!window.confirm('Boeking annuleren? De plek komt weer vrij; er is niets betaald.')) return;
    setBusy(true);
    setErrorText(null);
    try {
      const problem = await clearReader();
      const res = await cancelBooking({
        booking_id: cancelDepartureId,
        refund: 'none',
        scope: 'booking',
        reason: 'POS: betaling niet afgerond',
      });
      if (!res.ok) {
        setErrorText(res.error?.message ?? res.error?.error ?? 'Annuleren mislukt');
        return;
      }
      if (problem) {
        // The booking IS cancelled — say so, but do not close the panel on a
        // reassuring note while the device may still be asking for the money.
        setErrorText(
          `Boeking geannuleerd, maar de pinterminal reageerde niet (${problem}) ` +
          'Controleer of het bedrag nog op het scherm staat en maak hem leeg.',
        );
        return;
      }
      onCancelledBooking?.();
    } finally {
      setBusy(false);
    }
  };

  const eur = amountCents != null ? `€ ${(amountCents / 100).toFixed(2)}` : null;

  return (
    <div className="terminal-panel">
      <div className="terminal-panel-head">
        <div className="terminal-panel-title">Pinterminal</div>
        {readerLabel ? <div className="muted terminal-panel-reader">{readerLabel}</div> : null}
      </div>

      {eur ? <div className="terminal-panel-amount">{eur}</div> : null}
      {bookingNumber ? <div className="muted">{bookingNumber}</div> : null}

      {phase === 'starting' ? (
        <div className="terminal-panel-status">
          <span className="terminal-spinner" aria-hidden="true" /> Bedrag wordt naar de pinterminal gestuurd…
        </div>
      ) : null}

      {phase === 'busy' ? (
        <div className="terminal-panel-status">
          <span className="terminal-spinner" aria-hidden="true" /> De pinterminal is bezet met een andere betaling — wachten tot hij vrij is…
        </div>
      ) : null}

      {phase === 'waiting' ? (
        <div className="terminal-panel-status">
          <span className="terminal-spinner" aria-hidden="true" /> Wacht op de klant: kaart of telefoon tegen de pinterminal.
          {secondsLeft != null ? <span className="muted"> ({secondsLeft}s)</span> : null}
        </div>
      ) : null}

      {phase === 'settling' ? (
        <div className="terminal-panel-status">
          <span className="terminal-spinner" aria-hidden="true" /> Betaald ✓ — inchecken…
        </div>
      ) : null}

      {phase === 'done' ? (
        <div className="terminal-panel-status terminal-panel-ok">
          Betaald ✓{checkinDepartureId ? (checkedIn ? ' · Ingecheckt ✓' : ' · Inchecken niet gelukt — check handmatig in') : ''}
        </div>
      ) : null}

      {phase === 'declined' ? (
        <div className="error-card walkin-submit-error">
          Betaling niet gelukt{declineText ? ` (${declineText})` : ''}.
        </div>
      ) : null}
      {phase === 'timeout' ? (
        <div className="error-card walkin-submit-error">
          {readerReleased ? (
            <>
              Geen betaling ontvangen. De pinterminal is vrijgegeven en staat weer klaar voor
              de volgende klant — met "Opnieuw proberen" stuur je het bedrag opnieuw.
              {provisional ? ' Gebeurt er niets, dan wordt deze boeking vanzelf geannuleerd.' : ''}
            </>
          ) : (
            <>
              Nog geen betaling ontvangen op de pinterminal. Staat hij aan en toont hij het bedrag?
              (Een uitgeschakelde terminal kan bij Stripe nog even als "online" staan.)
            </>
          )}
        </div>
      ) : null}
      {phase === 'start_failed' ? (
        <div className="error-card walkin-submit-error">{errorText}</div>
      ) : null}
      {errorText && phase !== 'start_failed' ? (
        <div className="error-card walkin-submit-error">{errorText}</div>
      ) : null}

      {phase !== 'done' && phase !== 'settling' ? (
        <div className="walkin-step-actions terminal-panel-actions">
          {cancelDepartureId ? (
            <button
              className="btn-link terminal-cancel-booking"
              disabled={busy || phase === 'starting'}
              onClick={() => void handleCancelBooking()}
              title="Pinterminal vrijgeven en de boeking annuleren (niets betaald, plek komt vrij)"
            >
              Boeking annuleren
            </button>
          ) : null}
          {!abandonHidden ? (
            <button
              className="btn-link"
              disabled={busy || phase === 'starting'}
              onClick={() => void handleAbandon()}
              title="Pinterminal vrijgeven; de boeking blijft staan als 'Nog niet betaald' (betaallink)"
            >
              Anders afrekenen
            </button>
          ) : null}
          {(phase === 'declined' || phase === 'timeout' || phase === 'start_failed') ? (
            <button className="walkin-primary-btn" disabled={busy} onClick={() => void handleRetry()}>
              {busy ? 'Bezig…' : 'Opnieuw proberen'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
});
